import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import StepLabel from "../../atoms/CheckoutStepLabel/StepLabel";
import CustomerInformation from "./Steps/CustomerInformation";
import Button from "../../atoms/Button";
import OrderItems from "./Steps/OrderItems";
import { Link } from "react-router-dom";
import Shipping from "./Steps/Shipping";
import ReviewSubmit from "./Steps/ReviewSubmit";
import { createOrder } from "../../../store/slices/OrderSlice/OrderSlice";
import {
  setShippingAddress,
  setShowAddressValidation,
  validateAddress,
} from "../../../store/slices/CustomerSlice/CustomerSlice";
import ValidateAddressPopup from "../../atoms/AddressValidation/AddressValidator";
import { deepObjectEqual } from "../../../helpers/helperFunctions";
import { getCart } from "../../../store/slices/CartSlice/CartSlice";

const INIT_STEPS = [
  {
    id: 1,
    title: "Step 1",
    description: "Customer Information",
    current: "current",
    component: <CustomerInformation key={1} />,
  },
  {
    id: 2,
    title: "Step 2",
    description: "Order Items",
    current: "future",
    component: <OrderItems key={2} />,
  },
  {
    id: 3,
    title: "Step 3",
    description: "Freight and Shipping",
    current: "future",
    component: <Shipping key={3} />,
  },
  {
    id: 4,
    title: "Step 4",
    description: "Review and Submit",
    current: "future",
    component: <ReviewSubmit key={4} />,
  },
];

export default function CheckoutProcess() {
  const { cartItems } = useSelector((state) => state.cart);
  const navigate = useNavigate();
  const [steps, setSteps] = useState(INIT_STEPS);
  const [error, setError] = useState(false);

  const dispatch = useDispatch();

  const { collectingShippingInfo } = useSelector((state) => state.orders);

  const {
    showAddressValidation,
    validateAddress: validationObject,
    originalShippingAddress,
    customer,
    isDropShip,
  } = useSelector((state) => state.customer);

  const updateSteps = useCallback((newId) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => ({
        ...step,
        current:
          step.id === newId ? "current" : step.id < newId ? "past" : "future",
      }))
    );
  }, []);

  const useValidatedAddress = () => {
    dispatch(
      setShippingAddress({
        shippingAddress: {
          ...customer.shipping_address,
          street: validationObject.address_line_1,
          street_two: validationObject.address_line_2,
          city: validationObject.city,
          state: validationObject.state,
          zip_code: validationObject.postal_code,
        },
        isDropShip: !deepObjectEqual(
          originalShippingAddress,
          customer.shipping_address
        ),
      })
    );
    dispatch(setShowAddressValidation(false));
    updateSteps(2);
  };

  const handleStepChange = useCallback(
    (id, ref) => {
      if (ref === "next" && id === steps.length) {
        dispatch(createOrder()).then((response) => {
          if (!response?.payload?.err) {
            dispatch(getCart());
            navigate("/view-order/" + response.payload[0]?.peer_order_num);
          }
        });
      } else if (ref === "next" && id === 1) {
        // Handle step 1 to step 2 transition
        if (isDropShip) {
          // Only validate address if it's a drop ship
          dispatch(validateAddress());
        } else {
          // If not drop ship, proceed to next step without validation
          updateSteps(id + 1);
        }
      } else {
        // Handle all other step transitions
        if (ref === "next" && id < steps.length) updateSteps(id + 1);
        else if (ref === "past" && id > 1) updateSteps(id - 1);
        else if (id < steps.find((step) => step.current === "current").id)
          updateSteps(id);
      }
    },
    [steps.length, updateSteps, isDropShip, dispatch, navigate, steps]
  );

  useEffect(() => {
    if (cartItems.length === 0) navigate("/view-cart");
  }, [cartItems.length, navigate]);

  return (
    <>
      <div className="flex gap-10 mx-5 mt-5">
        {steps.map((step) => (
          <StepLabel
            key={step.id}
            {...step}
            onClick={() => handleStepChange(step.id)}
          />
        ))}
      </div>
      {steps
        .filter((step) => step.current === "current")
        .map((tab) =>
          React.cloneElement(tab.component, {
            key: tab.id,
            setParentError: setError,
          })
        )}
      <div className="flex justify-between">
        <Link
          to="/"
          className="text-sm font-medium text-[var(--primary-color)] hover:text-[var(--secondary-color)] ml-6 mt-6"
        >
          Back to Home
          <span aria-hidden="true"> &larr;</span>
        </Link>
        <div className="my-6 flex items-center justify-end gap-x-6 mr-6">
          <Button
            type="theme"
            textContent="Back"
            className="w-[10vw]"
            disabled={steps.find((step) => step.current === "current").id === 1}
            onClick={() =>
              handleStepChange(
                steps.find((step) => step.current === "current").id,
                "past"
              )
            }
          />

          <Button
            type="theme"
            textContent={
              steps.find((step) => step.current === "current").id ===
              steps.length
                ? "Submit"
                : "Next"
            }
            className="w-[10vw]"
            disabled={error || collectingShippingInfo}
            onClick={() =>
              handleStepChange(
                steps.find((step) => step.current === "current").id,
                "next"
              )
            }
          />
        </div>
      </div>

      <ValidateAddressPopup
        show={showAddressValidation}
        addressData={validationObject}
        onAcceptAddress={useValidatedAddress}
        onUseOriginal={() => {
          dispatch(setShowAddressValidation(false));
          updateSteps(2);
        }}
      />
    </>
  );
}
